import { motion, useReducedMotion } from 'framer-motion';
import { useSite } from '../content';

interface HeroProps {
  onOpenContact: (inquiryType?: string) => void;
}

export function Hero({ onOpenContact }: HeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const site = useSite();
  const { hero } = site;
  const fadeIn = prefersReducedMotion
    ? { initial: false as const, animate: { opacity: 1, y: 0 } }
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

  return (
    <section
      className="relative w-full h-screen min-h-[600px] flex flex-col overflow-hidden bg-slate-dark"
      aria-labelledby="hero-heading"
    >
      <h1 id="hero-heading" className="sr-only">
        {site.site.title} — {hero.tagline}
      </h1>

      {/*
        Background image is a <div> with a CSS background so the focal
        point can be tuned per breakpoint. Edit the bg-[...] utilities
        below to reframe per viewport (format: bg-[x_y], percentages,
        keywords like center/top/bottom, or px/rem).

        Keeps the screen-reader semantics of the old <img>: if backgroundAlt
        is empty, the div is treated as decorative (aria-hidden); if populated,
        it's announced as an image with that label.
      */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-[76%_center] md:bg-[75%] lg:bg-center"
        style={{ backgroundImage: `url(${hero.backgroundImageUrl})` }}
        role={hero.backgroundAlt ? 'img' : undefined}
        aria-label={hero.backgroundAlt || undefined}
        aria-hidden={hero.backgroundAlt ? undefined : true}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-black/15 to-transparent" />
      </div>

      <div className="absolute bottom-0 left-0 w-full h-20 z-20 bg-gradient-to-b from-transparent to-[#2A2420]" />

      <div className="relative z-10 absolute top-[14%] md:top-[18%] left-4 md:left-[14%] lg:left-[15%] flex flex-col items-start pr-4">
        <motion.div
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={{ duration: prefersReducedMotion ? 0 : 1.2, ease: 'easeOut' }}
          className="flex flex-col items-start"
        >
          <img
            src={hero.logoImageUrl}
            alt={hero.logoAlt}
            className="w-[260px] md:w-[460px] lg:w-[580px] h-auto -ml-2 md:-ml-4 lg:-ml-6"
          />

          <motion.p
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: prefersReducedMotion ? 0 : 1, delay: prefersReducedMotion ? 0 : 0.6 }}
            className="mt-4 md:mt-8 text-base md:text-2xl font-sans font-light text-textlight/80 tracking-wide"
          >
            {hero.tagline}
          </motion.p>

          <motion.button
            type="button"
            onClick={() => onOpenContact()}
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: prefersReducedMotion ? 0 : 1, delay: prefersReducedMotion ? 0 : 1 }}
            className="mt-5 md:mt-6 relative px-5 pt-2 pb-[0.8rem] rounded-full overflow-hidden group transition-all duration-300 min-h-[44px]"
            style={{
              background:
                'linear-gradient(160deg, #3A1C18 0%, #4A2520 25%, #351812 50%, #3E1E18 75%, #2E1410 100%)',
              boxShadow:
                '0 4px 14px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.06), inset 0 -2px 5px rgba(0,0,0,0.4)',
            }}
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full pointer-events-none opacity-30"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='w'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.15 0.8' numOctaves='5' seed='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23w)' opacity='0.4'/%3E%3C/svg%3E")`,
              }}
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full pointer-events-none opacity-10"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.15) 3px, rgba(0,0,0,0.15) 4px)',
              }}
            />
            <span className="relative z-10 font-sans text-xs md:text-sm tracking-wider text-textlight/90 group-hover:text-white transition-colors duration-300">
              {hero.ctaLabel}
            </span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
